(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document && root.customElements) api.install(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function normalizeCode(value) {
    return String(value == null ? '' : value)
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      .trim()
      .replace(/\s+/g, ' ')
      .toUpperCase()
      .replace(/^PANTONE\s+/, '')
      .replace(/\s*-\s*/g, '-')
      .replace(/(\d)(TCX|TPG|TPX|C|U)$/, '$1 $2');
  }

  function normalizeHex(value) {
    let hex = String(value == null ? '' : value).trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map((digit) => digit + digit).join('');
    return /^[0-9a-f]{6}$/i.test(hex) ? '#' + hex.toUpperCase() : null;
  }

  function resolveColor(value, palette) {
    const code = normalizeCode(value);
    const entry = (Array.isArray(palette) ? palette : []).find((item) => (
      item && normalizeCode(item.code) === code && normalizeHex(item.hex)
    ));
    if (code && entry) {
      return { status: 'known', code, hex: normalizeHex(entry.hex), name: String(entry.name || '') };
    }
    return { status: code ? 'unknown' : 'empty', code, hex: null, name: '' };
  }

  const REFERENCE_IMAGE_LIMITS = Object.freeze({
    maxBytes: 10 * 1024 * 1024,
    maxDimension: 8192,
    maxPixels: 16000000,
    sampleDimension: 1024,
  });

  function validateReferenceFile(file) {
    if (!file || !['image/jpeg', 'image/png', 'image/webp'].includes(String(file.type).toLowerCase())) {
      throw new Error('Choose a JPEG, PNG, or WebP image.');
    }
    if (!Number.isFinite(file.size) || file.size <= 0) throw new Error('The selected image is empty.');
    if (file.size > REFERENCE_IMAGE_LIMITS.maxBytes) throw new Error('Choose an image no larger than 10 MB.');
    return true;
  }

  function validateImageDimensions(width, height) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1
      || width > REFERENCE_IMAGE_LIMITS.maxDimension || height > REFERENCE_IMAGE_LIMITS.maxDimension) {
      throw new Error('Image dimensions must be between 1 and 8192 pixels per side.');
    }
    if (width * height > REFERENCE_IMAGE_LIMITS.maxPixels) throw new Error('Choose an image with no more than 16 megapixels.');
    return true;
  }

  function sampleCoordinates(clientX, clientY, rect, width, height) {
    if (!rect || !Number.isFinite(clientX) || !Number.isFinite(clientY)
      || !(rect.width > 0) || !(rect.height > 0) || !(width > 0) || !(height > 0)) return null;
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x: Math.min(width - 1, Math.floor(x * width)), y: Math.min(height - 1, Math.floor(y * height)) };
  }

  function pixelToHex(pixel) {
    if (!pixel || pixel.length < 4 || pixel[3] < 250) return null;
    const channels = Array.from(pixel).slice(0, 3);
    if (!channels.every((value) => Number.isFinite(value) && value >= 0 && value <= 255)) return null;
    return '#' + channels.map((value) => Math.round(value).toString(16).padStart(2, '0')).join('').toUpperCase();
  }

  function toLinear(channel) {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  }

  function toSrgb(value) {
    const clamped = Math.max(0, Math.min(1, value));
    return 255 * (clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055);
  }

  // A small histogram avoids sorting every pixel when the customer changes colors.
  function referenceLuminance(source, mask) {
    const histogram = new Uint32Array(1024);
    let count = 0;
    for (let offset = 0; offset < source.length; offset += 4) {
      if (mask[offset] * mask[offset + 3] < 32768 || source[offset + 3] === 0) continue;
      const luminance = 0.2126 * toLinear(source[offset])
        + 0.7152 * toLinear(source[offset + 1]) + 0.0722 * toLinear(source[offset + 2]);
      histogram[Math.min(1023, Math.round(luminance * 1023))] += 1;
      count += 1;
    }
    if (!count) return 0.18;
    let accumulated = 0;
    for (let index = 0; index < histogram.length; index += 1) {
      accumulated += histogram[index];
      if (accumulated >= Math.ceil(count / 2)) return Math.max(0.005, index / 1023);
    }
    return 0.18;
  }

  function recolorPixels(source, mask, targetHex, options) {
    if (!source || !mask || source.width !== mask.width || source.height !== mask.height) {
      throw new Error('Source image and leather mask dimensions must match.');
    }
    const length = source.width * source.height * 4;
    if (!Number.isInteger(source.width) || !Number.isInteger(source.height)
      || source.width < 1 || source.height < 1 || !source.data || !mask.data
      || source.data.length !== length || mask.data.length !== length) {
      throw new Error('Invalid image pixel data.');
    }
    const hex = normalizeHex(targetHex);
    if (!hex) throw new Error('Invalid target HEX color.');
    // A photographed black/white surface still needs room for texture and light.
    // Keep ordinary screen colors unchanged; reserve headroom only at the extremes.
    const target = [1, 3, 5].map((index) => Math.max(16, Math.min(239,
      parseInt(hex.slice(index, index + 2), 16))));
    const configuredReference = Number(options && options.referenceLuminance);
    const reference = configuredReference > 0 && configuredReference <= 1
      ? configuredReference : referenceLuminance(source.data, mask.data);
    const referenceTone = toSrgb(reference);
    const result = new Uint8ClampedArray(source.data);

    for (let offset = 0; offset < length; offset += 4) {
      const weight = mask.data[offset] / 255 * mask.data[offset + 3] / 255;
      if (!weight || source.data[offset + 3] === 0) continue;
      const luminance = 0.2126 * toLinear(source.data[offset])
        + 0.7152 * toLinear(source.data[offset + 1]) + 0.0722 * toLinear(source.data[offset + 2]);
      // Perceptual brightness differences preserve fine grain across both dark
      // and light colors; linear-light ratios exaggerate bright texture patches.
      const toneDifference = (toSrgb(luminance) - referenceTone) * 0.75;
      for (let channel = 0; channel < 3; channel += 1) {
        // Matching slopes through the reference tone avoid the old white veil.
        // Smooth toes/shoulders retain texture without hard clipping at 0 or 255.
        const tone = target[channel];
        const transformed = toneDifference < 0 ? tone * Math.exp(toneDifference / tone)
          : tone + (255 - tone) * (1 - Math.exp(-toneDifference / (255 - tone)));
        result[offset + channel] = source.data[offset + channel] * (1 - weight) + transformed * weight;
      }
      // Alpha, the background, and excluded hardware are never changed.
    }
    return { width: source.width, height: source.height, data: result };
  }

  function install(window) {
    if (!window.customElements || window.customElements.get('aarons-color-preview')) return;
    const document = window.document;
    const fieldNames = {
      code: 'contact[Requested color code]',
      hex: 'contact[Color preview HEX]',
      basis: 'contact[Color preview basis]',
    };

    class AaronsColorPreview extends window.HTMLElement {
      connectedCallback() {
        if (this._active) return;
        this._active = true;
        this._connection = (this._connection || 0) + 1;
        this._renderSequence = 0;
        this._sampleSequence = (this._sampleSequence || 0) + 1;
        this._abort = new window.AbortController();
        this._selection = { code: '', hex: '', basis: '' };
        this._selectionLabel = '';
        this._messages = {};
        const localized = this.querySelector('script[data-preview-i18n]');
        if (localized) {
          try {
            const messages = JSON.parse(localized.textContent);
            if (messages && typeof messages === 'object' && !Array.isArray(messages)) this._messages = messages;
          } catch (_) { /* Keep the original English behavior if translation data is unavailable. */ }
        }
        this._canvas = this.querySelector('[data-preview-canvas]');
        this._original = this.querySelector('[data-preview-original]');
        this._code = this.querySelector('[data-preview-code]');
        this._hex = this.querySelector('[data-preview-hex]');
        this._hexText = this.querySelector('[data-preview-hex-text]');
        this._initialHex = normalizeHex(this._hex && this._hex.defaultValue) || '#B5AAA0';
        this._status = this.querySelector('[data-preview-status]');
        this._current = this.querySelector('[data-preview-current]');
        this._basis = this.querySelector('[data-preview-basis]');
        this._file = this.querySelector('[data-preview-file]');
        this._sampleContainer = this.querySelector('[data-preview-sample-container]');
        this._sampleCanvas = this.querySelector('[data-preview-sample-canvas]');
        this._sampleStatus = this.querySelector('[data-preview-sample-status]');
        const listen = (element, event, handler) => {
          if (element) element.addEventListener(event, handler, { signal: this._abort.signal });
        };
        listen(this._original, 'error', () => this._markOriginalUnavailable());
        listen(this._code, 'input', () => this._recordCode());
        listen(this._code, 'keydown', (event) => {
          if (event.key === 'Enter') { event.preventDefault(); this._applyCode(); }
        });
        listen(this.querySelector('[data-preview-apply]'), 'click', (event) => {
          event.preventDefault(); this._applyCode();
        });
        listen(this._hex, 'input', () => this._applyHex(this._hex.value));
        listen(this._hexText, 'keydown', (event) => {
          if (event.key === 'Enter') { event.preventDefault(); this._applyHex(this._hexText.value); }
        });
        listen(this.querySelector('[data-preview-hex-apply]'), 'click', (event) => {
          event.preventDefault(); this._applyHex(this._hexText && this._hexText.value);
        });
        listen(this._file, 'change', () => {
          const file = this._file.files && this._file.files[0];
          if (file) this._loadSample(file);
        });
        listen(this.querySelector('[data-preview-sample-clear]'), 'click', (event) => {
          event.preventDefault(); this._clearSample();
        });
        listen(this._sampleCanvas, 'click', (event) => {
          const point = sampleCoordinates(event.clientX, event.clientY, this._sampleCanvas.getBoundingClientRect(), this._sampleCanvas.width, this._sampleCanvas.height);
          if (point) this._sampleAt(point.x, point.y);
        });
        listen(this._sampleCanvas, 'keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this._sampleAt(Math.floor(this._sampleCanvas.width / 2), Math.floor(this._sampleCanvas.height / 2));
          }
        });
        listen(this.querySelector('[data-preview-reset]'), 'click', (event) => {
          event.preventDefault(); this._reset();
        });
        listen(this.querySelector('[data-preview-request]'), 'click', (event) => this._request(event));
        this.querySelectorAll('[data-preview-swatch]').forEach((button) => {
          listen(button, 'click', (event) => {
            event.preventDefault(); this._applyHex(button.dataset.hex, button.dataset.name || '');
          });
        });
        this._showOriginal();
        this._syncRequest();
        if (this._original && this._original.complete && this._original.naturalWidth === 0) {
          this._markOriginalUnavailable();
        }
        if (this._canPreview() && window.IntersectionObserver) {
          this._observer = new window.IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this._observer.disconnect();
              this._loadPixels().catch(() => { /* The original image stays available. */ });
            }
          }, { rootMargin: '150px' });
          this._observer.observe(this);
        }
      }

      disconnectedCallback() {
        this._active = false;
        this._renderSequence += 1;
        this._clearSample();
        if (this._abort) this._abort.abort();
        if (this._observer) this._observer.disconnect();
        this._loadPromise = null;
        this._pixels = null;
      }

      _translateDisplay(message) {
        const translated = this._messages && Object.prototype.hasOwnProperty.call(this._messages, message)
          ? this._messages[message] : null;
        return typeof translated === 'string' ? translated : message;
      }

      _translateBasis(message) {
        return String(message || '').split('; ').map((part) => this._translateDisplay(part)).join('; ');
      }

      _formatMessage(template, values) {
        return this._translateDisplay(template).replace(/\{([a-z]+)\}/g, (token, key) => (
          Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
        ));
      }

      _setStatus(message) {
        if (this._status) this._status.textContent = this._translateDisplay(message);
      }

      _showOriginal() {
        if (this._canvas) this._canvas.hidden = true;
        if (this._original) this._original.hidden = !!this._originalFailed;
      }

      _markOriginalUnavailable() {
        if (!this._active || this._originalFailed) return;
        this._originalFailed = true;
        this._renderSequence += 1;
        this.dataset.previewEnabled = 'false';
        this._pixels = null;
        if (this._observer) this._observer.disconnect();
        const visual = this.querySelector('.aarons-color-preview__visual');
        const workspace = this.querySelector('.aarons-color-preview__workspace');
        if (visual) visual.hidden = true;
        if (workspace) workspace.classList.add('aarons-color-preview__workspace--request-only');
        this._showOriginal();
        if (this._selection.hex) this._selection.basis += '; product preview unavailable';
        this._updateLabels(this._selectionLabel || this._selection.hex || 'No custom color selected', this._selection.basis || 'No custom color selected');
        this._syncRequest();
        this._setStatus('The product preview image is unavailable. You can still select a screen color and save your color reference for the inquiry.');
      }

      _canPreview() {
        return !this._originalFailed && this.dataset.previewEnabled !== 'false' && !!this.dataset.baseUrl && !!this.dataset.maskUrl
          && !!this._canvas && !!this._original;
      }

      _updateLabels(label, basis) {
        if (this._current) this._current.textContent = this._translateDisplay(label);
        if (this._basis) this._basis.textContent = this._translateBasis(basis);
        this.querySelectorAll('[data-preview-swatch]').forEach((button) => {
          button.setAttribute('aria-pressed', String(
            !!this._selection.hex && normalizeHex(button.dataset.hex) === this._selection.hex
          ));
        });
      }

      _recordCode() {
        // A reference is an independent inquiry note, never a color-library lookup.
        this._selection.code = normalizeCode(this._code && this._code.value);
        if (!this._selection.hex) this._selection.basis = this._selection.code ? 'Reference code only; no color conversion or verified match' : '';
        this._updateLabels(this._selection.hex ? this._selectionLabel || this._selection.hex : 'No screen color selected', this._selection.basis || 'No custom color selected');
        this._setStatus(this._selection.code
          ? 'Color reference saved for your inquiry only. The screen color is selected separately; no Pantone match is verified.'
          : this._selection.hex ? 'Reference cleared. Your independently selected screen color is unchanged.' : 'Enter a color reference, or select a screen color.');
        this._syncRequest();
      }

      _applyCode() {
        if (this._code) this._code.value = normalizeCode(this._code.value);
        this._recordCode();
      }

      _applyHex(value, name, basis) {
        const hex = normalizeHex(value);
        if (!hex) { this._setStatus('Enter a valid three- or six-digit HEX color, for example #AABBCC. Your previous color is unchanged.'); return; }
        this._renderSequence += 1;
        if (this._hex) this._hex.value = hex;
        if (this._hexText) this._hexText.value = hex;
        this._selection = {
          code: normalizeCode(this._code ? this._code.value : this._selection.code),
          hex,
          basis: basis || 'Independent screen HEX; not a verified Pantone match',
        };
        this._selectionLabel = (name ? this._translateDisplay(name) + ' — ' : '') + hex;
        if (!this._canPreview()) this._selection.basis += '; product recolor not enabled';
        this._updateLabels(this._selectionLabel, this._selection.basis);
        this._syncRequest();
        if (!this._canPreview()) {
          this._setStatus('Your screen color request is saved for the inquiry. A product recolor is not shown for this item. Final color is confirmed with a leather sample.');
          return;
        }
        this._render(hex, 'Independent screen color preview shown; no Pantone match is verified. Final color is confirmed with the approved leather sample.');
      }

      _reset() {
        this._renderSequence += 1;
        if (this._code) this._code.value = '';
        if (this._hex) this._hex.value = this._initialHex;
        if (this._hexText) this._hexText.value = '';
        this._clearSample();
        this._selection = { code: '', hex: '', basis: '' };
        this._selectionLabel = '';
        this._showOriginal();
        this._updateLabels(this._canPreview() ? 'Original product image' : 'No custom color selected', 'No custom color selected');
        this._setStatus(this._canPreview() ? 'Original product image restored. Custom color request cleared.' : 'Custom color request cleared.');
        this._syncRequest();
      }

      _form() {
        return document.querySelector('#product-rfq form');
      }

      _syncRequest() {
        const form = this._form();
        if (form) {
          Object.keys(fieldNames).forEach((key) => {
            const name = fieldNames[key];
            let field = Array.from(form.querySelectorAll('input')).find((input) => input.name === name);
            if (!field) {
              field = document.createElement('input');
              field.type = 'hidden';
              field.name = name;
              field.dataset.colorPreviewField = key;
              form.appendChild(field);
            }
            field.value = this._selection[key];
          });
          const summary = form.querySelector('[data-rfq-color-summary]');
          if (summary) {
            const parts = [];
            if (this._selection.code) parts.push(this._translateDisplay('Color reference note:') + ' ' + this._selection.code);
            if (this._selection.hex) parts.push(this._translateDisplay('Screen color HEX:') + ' ' + this._selection.hex);
            if (this._selection.basis) parts.push(this._translateBasis(this._selection.basis));
            summary.textContent = parts.join(' · ');
            summary.hidden = parts.length === 0;
          }
        }
        this.dispatchEvent(new window.CustomEvent('aarons:color-preview', {
          bubbles: true,
          detail: { productId: this.dataset.productId || '', ...this._selection },
        }));
      }

      _request(event) {
        this._syncRequest();
        if (!this._selection.code && !this._selection.hex) {
          event.preventDefault();
          this._setStatus('Enter a color code or select a preview color before requesting it.');
          if (this._code) this._code.focus({ preventScroll: true });
          return;
        }
        const form = this._form();
        if (!form) {
          event.preventDefault();
          this._setStatus('Your color selection is ready. Please include it when you contact our sales team.');
          return;
        }
        this._setStatus('Your color request has been added to the inquiry form. Complete your contact details to send it.');
        const contactField = form.querySelector('input[type="email"], input[name="contact[Name]"]');
        if (contactField) contactField.focus({ preventScroll: true });
        // The existing #product-rfq link provides navigation. Never submit automatically.
      }

      _setSampleStatus(message) {
        if (this._sampleStatus) this._sampleStatus.textContent = this._translateDisplay(message);
      }

      _clearSample(options) {
        this._sampleSequence = (this._sampleSequence || 0) + 1;
        if (this._sampleAbort) { this._sampleAbort.abort(); this._sampleAbort = null; }
        if (this._sampleObjectUrl) {
          window.URL.revokeObjectURL(this._sampleObjectUrl);
          this._sampleObjectUrl = null;
        }
        this._samplePixels = null;
        if (this._sampleContainer) this._sampleContainer.hidden = true;
        if (this._sampleCanvas) { this._sampleCanvas.width = 1; this._sampleCanvas.height = 1; }
        if (this._file && (!options || options.clearInput !== false)) this._file.value = '';
        if (!options || options.announce !== false) {
          this._setSampleStatus('Reference image cleared. Any previously selected screen color remains in your inquiry.');
        }
      }

      async _loadSample(file) {
        try {
          validateReferenceFile(file);
        } catch (error) {
          this._setSampleStatus(error.message);
          return;
        }
        this._clearSample({ clearInput: false, announce: false });
        const sequence = this._sampleSequence;
        const connection = this._connection;
        let objectUrl;
        try {
          if (!window.URL || !window.URL.createObjectURL) throw new Error('Local image sampling is not supported by this browser.');
          objectUrl = window.URL.createObjectURL(file);
          this._sampleObjectUrl = objectUrl;
          this._sampleAbort = new window.AbortController();
          this._setSampleStatus('Reading your reference image locally. The file is not uploaded.');
          const image = await this._loadImage(objectUrl, this._sampleAbort.signal);
          if (!this._active || connection !== this._connection || sequence !== this._sampleSequence) return;
          validateImageDimensions(image.naturalWidth, image.naturalHeight);
          if (!this._sampleCanvas) throw new Error('The local sampling area is not available.');
          const scale = Math.min(1, REFERENCE_IMAGE_LIMITS.sampleDimension / Math.max(image.naturalWidth, image.naturalHeight));
          this._sampleCanvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          this._sampleCanvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          const context = this._sampleCanvas.getContext('2d', { willReadFrequently: true });
          if (!context) throw new Error('Canvas sampling is not supported by this browser.');
          context.drawImage(image, 0, 0, this._sampleCanvas.width, this._sampleCanvas.height);
          this._samplePixels = context.getImageData(0, 0, this._sampleCanvas.width, this._sampleCanvas.height);
          if (this._sampleContainer) this._sampleContainer.hidden = false;
          this._setSampleStatus('Click a solid-color area to select its screen color. Press Enter or Space on the image to sample its center. The image stays in your browser only.');
        } catch (error) {
          if (!this._active || connection !== this._connection || sequence !== this._sampleSequence) return;
          this._samplePixels = null;
          if (this._sampleContainer) this._sampleContainer.hidden = true;
          this._setSampleStatus(error.message || 'The reference image could not be read. Your current color request is unchanged.');
        } finally {
          if (objectUrl && this._sampleObjectUrl === objectUrl) {
            window.URL.revokeObjectURL(objectUrl);
            this._sampleObjectUrl = null;
          }
          if (connection === this._connection && sequence === this._sampleSequence) this._sampleAbort = null;
        }
      }

      _sampleAt(x, y) {
        const pixels = this._samplePixels;
        if (!pixels || !Number.isInteger(x) || !Number.isInteger(y)
          || x < 0 || y < 0 || x >= pixels.width || y >= pixels.height) return;
        const offset = (y * pixels.width + x) * 4;
        const hex = pixelToHex(pixels.data.subarray(offset, offset + 4));
        if (!hex) {
          this._setSampleStatus('Choose an opaque solid-color area. Transparent or partly transparent pixels cannot be used as a reliable color reference.');
          return;
        }
        this._applyHex(hex, 'Image sample', 'Customer image color sampled locally; not a verified Pantone match');
        this._setSampleStatus(this._formatMessage('Selected {hex} from your image locally. The image is not attached to your inquiry; final leather color requires sample approval.', { hex }));
      }

      _loadImage(url, loadSignal) {
        return new Promise((resolve, reject) => {
          const image = new window.Image();
          const signal = loadSignal || this._abort.signal;
          const cleanup = () => {
            image.onload = null;
            image.onerror = null;
            signal.removeEventListener('abort', aborted);
          };
          const aborted = () => {
            cleanup();
            if (image.removeAttribute) image.removeAttribute('src');
            reject(new Error('Color preview image load canceled.'));
          };
          image.crossOrigin = 'anonymous';
          image.onload = () => { cleanup(); resolve(image); };
          image.onerror = () => { cleanup(); reject(new Error('Color preview image could not be loaded.')); };
          signal.addEventListener('abort', aborted, { once: true });
          if (signal.aborted) { aborted(); return; }
          image.src = url;
        });
      }

      _loadPixels() {
        if (this._pixels) return Promise.resolve(this._pixels);
        if (this._loadPromise) return this._loadPromise;
        const connection = this._connection;
        const baseUrl = this.dataset.baseUrl;
        const maskUrl = this.dataset.maskUrl;
        if (!baseUrl || !maskUrl) return Promise.reject(new Error('Preview image or leather mask is missing.'));
        const promise = Promise.all([this._loadImage(baseUrl), this._loadImage(maskUrl)]).then(([base, mask]) => {
          if (!this._active || connection !== this._connection || this._originalFailed) throw new Error('Color preview is no longer available.');
          if (!base.naturalWidth || base.naturalWidth !== mask.naturalWidth || base.naturalHeight !== mask.naturalHeight) {
            throw new Error('Preview image and leather mask dimensions do not match.');
          }
          const readImage = (image) => {
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const context = canvas.getContext('2d', { willReadFrequently: true });
            if (!context) throw new Error('Canvas color preview is not available.');
            context.drawImage(image, 0, 0);
            return context.getImageData(0, 0, canvas.width, canvas.height);
          };
          const source = readImage(base);
          const maskData = readImage(mask);
          let hasCoverage = false;
          for (let offset = 0; offset < maskData.data.length; offset += 4) {
            if (maskData.data[offset] > 0 && maskData.data[offset + 3] > 0 && source.data[offset + 3] > 0) {
              hasCoverage = true;
              break;
            }
          }
          if (!hasCoverage) throw new Error('The leather mask has no visible recoloring coverage.');
          const configuredReference = Number(this.dataset.referenceLuminance);
          const reference = configuredReference > 0 && configuredReference <= 1
            ? configuredReference : referenceLuminance(source.data, maskData.data);
          this._pixels = { source, mask: maskData, reference };
          return this._pixels;
        });
        this._loadPromise = promise;
        promise.catch(() => {
          if (this._loadPromise === promise) this._loadPromise = null;
        });
        return promise;
      }

      async _render(hex, successMessage) {
        if (!this._canPreview()) {
          this._setStatus('Your screen color request is saved for the inquiry. A product recolor is not shown for this item.');
          return;
        }
        const sequence = this._renderSequence;
        const connection = this._connection;
        this._showOriginal();
        this._setStatus('Preparing the digital color preview. Your original product image remains available.');
        try {
          const pixels = await this._loadPixels();
          if (!this._active || connection !== this._connection || sequence !== this._renderSequence) return;
          if (!this._canvas) throw new Error('Preview canvas is missing.');
          const result = recolorPixels(pixels.source, pixels.mask, hex, { referenceLuminance: pixels.reference });
          this._canvas.width = result.width;
          this._canvas.height = result.height;
          const context = this._canvas.getContext('2d');
          if (!context) throw new Error('Preview canvas is unavailable.');
          const frame = context.createImageData(result.width, result.height);
          frame.data.set(result.data);
          context.putImageData(frame, 0, 0);
          this._canvas.hidden = false;
          if (this._original) this._original.hidden = true;
          this._setStatus(successMessage);
        } catch (_) {
          if (!this._active || connection !== this._connection || sequence !== this._renderSequence) return;
          this._showOriginal();
          this._selection.basis = (this._selection.basis || 'Independent screen HEX; not a verified Pantone match') + '; product preview unavailable';
          this._updateLabels(this._selectionLabel || this._selection.hex, this._selection.basis);
          this._syncRequest();
          this._setStatus('The digital preview is unavailable. Your color request is saved for the inquiry, and the original product image is shown.');
        }
      }
    }

    window.customElements.define('aarons-color-preview', AaronsColorPreview);
  }

  return {
    normalizeCode, normalizeHex, resolveColor, recolorPixels,
    validateReferenceFile, validateImageDimensions, sampleCoordinates, pixelToHex,
    REFERENCE_IMAGE_LIMITS, install,
  };
});
